import * as React from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTitanApp } from "@/hooks/useTitanApp";

interface VaccineComboboxProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Fired when the user explicitly selects an item from the list (either via
   * mouse or keyboard). Similar to `onChange` but guarantees the value came
   * from the dropdown rather than typed by hand.
   */
  onSelect?: (value: string) => void;
  placeholder?: string;
  isDarkMode?: boolean;
  disabled?: boolean;
  className?: string;
  onEnterKey?: () => void;
}

export function VaccineCombobox({
  value,
  onChange,
  onSelect,
  placeholder = "Select vaccine...",
  isDarkMode = false,
  disabled = false,
  className,
  onEnterKey,
}: VaccineComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { nmsServiceState } = useTitanApp();
  const { companyVaccines } = nmsServiceState || {};

  const flattenedProducts = React.useMemo(() => {
    const results = [];
    const seenIds = new Set();
    (companyVaccines || []).forEach((item: any) => {
      const vaccine =
        item?.value && typeof item.value === "object" ? item.value : item;
      if (vaccine && vaccine.vaccineName) {
        (vaccine.products || []).forEach((product: any) => {
          const uniqueId = `${vaccine._id}-${product.productId}`;
          if (!seenIds.has(uniqueId)) {
            results.push({
              ...vaccine,
              selectedProduct: product,
              uniqueId,
            });
            seenIds.add(uniqueId);
          }
        });
      }
    });
    return results;
  }, [companyVaccines]);

  const searchedProducts = React.useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return flattenedProducts;

    const terms = query.split(/\s+/).filter(Boolean);
    if (terms.length === 0) return flattenedProducts;

    return flattenedProducts.filter((item: any) => {
      const vaccineName = (item.vaccineName || "").toLowerCase();
      const productName = (
        item.selectedProduct?.productName || ""
      ).toLowerCase();
      const manufacturerName = (
        item.selectedProduct?.manufacturerName || ""
      ).toLowerCase();

      const searchString = `${vaccineName} ${productName} ${manufacturerName}`;

      return terms.every((term) => searchString.includes(term));
    });
  }, [flattenedProducts, searchQuery]);

  // Get display text for selected vaccine
  const selectedVaccineDisplay = React.useMemo(() => {
    if (!value) return placeholder;

    const selectedProduct = flattenedProducts.find((v: any) => v.uniqueId === value);
    return selectedProduct ? selectedProduct.vaccineName : value;
  }, [value, placeholder, flattenedProducts]);

  // The Popover component handles click-outside behavior automatically,
  // so we don't need a custom click-outside handler

  // Reset highlighted index when search results change
  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery]);

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(
        `[data-index="${highlightedIndex}"]`,
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [highlightedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Enter" && value && onEnterKey) {
        e.preventDefault();
        onEnterKey();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < searchedProducts.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && searchedProducts[highlightedIndex]) {
          const selectedItem = searchedProducts[highlightedIndex];
          onChange(selectedItem.uniqueId);
          if (onSelect) {
            onSelect(selectedItem.uniqueId);
          }
          setOpen(false);
          setSearchQuery("");
          setHighlightedIndex(-1);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
        break;
      case "Tab":
        setOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleVaccineSelect = (vaccineId: string) => {
    onChange(vaccineId);
    setOpen(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !open && value && onEnterKey) {
              e.preventDefault();
              onEnterKey();
            }
          }}
          className={cn(
            "w-full justify-between",
            !value && "text-muted-foreground",
            isDarkMode
              ? "bg-gray-900 border-gray-600 text-gray-200 hover:bg-gray-800"
              : "bg-white border-gray-300",
            className,
          )}
        >
          <span className="truncate text-sm">{selectedVaccineDisplay}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "w-[--radix-popover-trigger-width] p-0 z-[10000]",
          isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white",
        )}
        align="start"
        side="bottom"
        sideOffset={4}
        avoidCollisions={true}
        collisionPadding={16}
      >
        <div
          ref={dropdownRef}
          className={cn(
            "flex flex-col w-full max-h-[300px]",
            isDarkMode ? "bg-gray-900" : "bg-white",
          )}
        >
          <div
            className={cn(
              "flex items-center px-3 py-2 border-b",
              isDarkMode ? "border-gray-700" : "border-gray-200",
            )}
          >
            <Search className="h-4 w-4 opacity-50 mr-2" />
            <Input
              ref={inputRef}
              autoFocus
              placeholder="Search vaccines..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className={cn(
                "border-0 bg-transparent focus-visible:ring-0 text-sm",
                isDarkMode
                  ? "text-gray-200 placeholder:text-gray-500"
                  : "text-gray-900 placeholder:text-gray-400",
              )}
            />
          </div>

          <ScrollArea className="h-[250px] w-full">
            <div className="p-1 w-full">
              {searchedProducts.length === 0 ? (
                <div
                  className={cn(
                    "px-3 py-6 text-center text-sm",
                    isDarkMode ? "text-gray-400" : "text-gray-500",
                  )}
                >
                  No vaccines found.
                </div>
              ) : (
                searchedProducts.map((item, index) => (
                  <button
                    type="button"
                    key={item.uniqueId}
                    data-index={index}
                    onClick={(e) => {
                      e.preventDefault();
                      onChange(item.uniqueId);
                      if (onSelect) {
                        onSelect(item.uniqueId);
                      }
                      setOpen(false);
                      setSearchQuery("");
                      setHighlightedIndex(-1);
                    }}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors cursor-pointer",
                      highlightedIndex === index
                        ? isDarkMode
                          ? "bg-gray-700 text-gray-100"
                          : "bg-blue-100 text-blue-900"
                        : isDarkMode
                          ? "text-gray-300 hover:bg-gray-800"
                          : "text-gray-900 hover:bg-gray-100",
                      value === item.uniqueId &&
                        (isDarkMode
                          ? "font-semibold bg-gray-700"
                          : "font-semibold bg-blue-50"),
                    )}
                  >
                    <Check
                      className={cn(
                        "h-4 w-4 shrink-0",
                        value === item.uniqueId ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="flex-1 truncate">
                      {item.vaccineName} - {item.selectedProduct?.productName} ({item.selectedProduct?.manufacturerName})
                    </span>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
}
